import { api } from '@/services/api'
import {
  PublicProfileResponse as PublicProfileResponseSchema,
  UpdatePrivacyResponse as UpdatePrivacyResponseSchema,
} from '@/shared-api'
import type { z } from 'zod'

const validateShape = (label: string, schema: z.ZodTypeAny, data: unknown) => {
  const r = schema.safeParse(data)
  if (!r.success) {
    // eslint-disable-next-line no-console
    console.error(`[shared-api drift] ${label}`, r.error.issues)
  }
}

export type IdeaStatus = 'live' | 'processed' | 'archived'

export type PublicProfileIdea = {
  id: string
  title: string
  one_line_pitch: string | null
  industry: string | null
  status: IdeaStatus
  overall_score: number | null
  number_of_upvotes: number
  created_at: string
  /** Live top-level comment count. Attached by the profile endpoint. */
  feedback_count?: number
  /** Live distinct-viewer count. Attached by the profile endpoint. */
  view_count?: number
}

// Ideas the profile owner has validated — same slim card plus the idea's own
// author (these belong to other founders, masked when submitted anonymously).
export type PublicProfileValidatedIdea = PublicProfileIdea & {
  author: string
}

export type PublicProfileStats = {
  points: number
  ideas_submitted: number
  ideas_validated: number
}

export type PublicProfile = {
  handle: string
  full_name: string | null
  industry: string | null
  industry_group?: string | null
  profession: string | null
  persona?: string | null
  city: string | null
  pronoun?: string | null
  age_range?: string | null
  bio?: string | null
  company_name?: string | null
  startup_stage?: string | null
  business_segment?: string | null
  looking_for?: string[]
  joined_at: string
  is_profile_public: boolean
  stats: PublicProfileStats
  ideas: PublicProfileIdea[]
  ideas_validated_list: PublicProfileValidatedIdea[]
}

type PublicProfileEnvelope = { success: boolean; data: PublicProfile }
type UpdatePrivacyEnvelope = {
  success: boolean
  data: { is_profile_public: boolean }
}
type UpdateHandleEnvelope = { success: boolean; data: unknown }

/**
 * GET /user/:handle — fetch another user's public profile. Backend gates
 * this on a valid session (any logged-in user can read), so callers must
 * be authenticated. Returns 404 for unknown handles, 403 if private.
 */
export async function fetchPublicProfile(
  handle: string,
): Promise<PublicProfile> {
  const { data } = await api.get<PublicProfileEnvelope>(
    `/user/${encodeURIComponent(handle)}`,
  )
  validateShape('GET /user/:handle', PublicProfileResponseSchema, data)
  return data.data
}

/**
 * GET /user/me — minimal shape pulled into the header / Redux auth user so we
 * can build links to the current user's public profile without re-fetching.
 */
export type MyProfileSummary = {
  id: string
  email: string
  handle: string | null
  full_name: string | null
  is_admin: boolean
}

export async function fetchMyProfile(): Promise<MyProfileSummary> {
  // Backend returns the DTO body directly (not wrapped in { success, data }),
  // but tolerate both shapes in case the controller is updated later.
  const { data } = await api.get<Record<string, unknown>>('/user/me')
  const raw = data ?? {}
  const u = (
    typeof raw.data === 'object' && raw.data !== null
      ? (raw.data as Record<string, unknown>)
      : raw
  ) as Record<string, unknown>
  const str = (v: unknown): string | null =>
    typeof v === 'string' && v.length > 0 ? v : null
  return {
    id: String(u.id ?? ''),
    email: str(u.email) ?? '',
    handle: str(u.handle),
    full_name: str(u.full_name),
    is_admin: u.is_admin === true,
  }
}

/** PATCH /user/me/handle — claim a new handle. 409 if taken or reserved. */
export async function updateHandle(handle: string): Promise<void> {
  await api.patch<UpdateHandleEnvelope>('/user/me/handle', { handle })
}

/** Minimal user shape returned by /user/search for @mention autocomplete. */
export type MentionSearchUser = {
  id: string
  handle: string
  full_name: string | null
}

/**
 * Best-effort profile handle lookup when an idea payload omits creator.handle.
 * Matches /user/search results by exact full_name (case-insensitive).
 */
export async function resolvePublicHandleByFullName(
  fullName: string,
): Promise<string | null> {
  const name = fullName.trim()
  if (!name || name.toLowerCase() === 'anonymous') return null

  const firstToken = name.split(/\s+/)[0]?.replace(/[^a-zA-Z0-9_]/g, '')
  if (!firstToken || firstToken.length < 2) return null

  try {
    const users = await searchMentionUsers(firstToken, 20)
    const exact = users.find(
      (u) => u.full_name?.trim().toLowerCase() === name.toLowerCase(),
    )
    return exact?.handle?.trim() || null
  } catch {
    return null
  }
}

/** GET /user/search — prefix-match handles for @mention autocomplete. */
export async function searchMentionUsers(
  q: string,
  limit = 10,
): Promise<MentionSearchUser[]> {
  const trimmed = q.replace(/^@/, '').trim()
  if (!trimmed) return []
  const { data } = await api.get<{
    success: boolean
    data: MentionSearchUser[]
  }>('/user/search', { params: { q: trimmed, limit } })
  return data.data
}

/** GET /user/handle/check — live availability lookup. Excludes own handle. */
export async function checkHandleAvailable(handle: string): Promise<boolean> {
  const { data } = await api.get<{
    success: boolean
    data: { available: boolean }
  }>('/user/handle/check', { params: { handle } })
  return data.data.available
}

/** Editable subset of /user/me. Empty string clears bio; undefined keeps it. */
export type UpdateProfileInput = Partial<{
  full_name: string
  city: string
  age_range: string
  pronoun: string
  industry: string
  industry_group: string
  profession: string
  persona: string
  bio: string
  startup_status: string
  company_name: string
  company_name_public: boolean
  startup_stage: string
  business_segment: string
  looking_for: string[]
  mobile_number: string
  newsletter_opt_in: boolean
}>

/** PATCH /user/me — update one or more profile fields. */
export async function updateProfile(input: UpdateProfileInput): Promise<void> {
  await api.patch<UpdateHandleEnvelope>('/user/me', input)
}

/** Full /user/me shape — used by EditProfilePage to seed the form. */
export type MyProfileFull = {
  id: string
  email: string
  handle: string | null
  full_name: string | null
  city: string | null
  age_range: string | null
  pronoun: string | null
  industry: string | null
  industry_group: string | null
  profession: string | null
  persona: string | null
  bio: string | null
  is_profile_public: boolean
  startup_status: string | null
  company_name: string | null
  company_name_public: boolean
  startup_stage: string | null
  business_segment: string | null
  looking_for: string[]
  mobile_number: string | null
  newsletter_opt_in: boolean
}

export async function fetchMyProfileFull(): Promise<MyProfileFull> {
  const { data } = await api.get<Record<string, unknown>>('/user/me')
  const raw = data ?? {}
  const u = (
    typeof raw.data === 'object' && raw.data !== null
      ? (raw.data as Record<string, unknown>)
      : raw
  ) as Record<string, unknown>
  const str = (v: unknown): string | null =>
    typeof v === 'string' && v.length > 0 ? v : null
  const arr = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  return {
    id: String(u.id ?? ''),
    email: str(u.email) ?? '',
    handle: str(u.handle),
    full_name: str(u.full_name),
    city: str(u.city),
    age_range: str(u.age_range),
    pronoun: str(u.pronoun),
    industry: str(u.industry),
    industry_group: str(u.industry_group),
    profession: str(u.profession),
    persona: str(u.persona),
    bio: str(u.bio),
    is_profile_public: u.is_profile_public !== false,
    startup_status: str(u.startup_status),
    company_name: str(u.company_name),
    company_name_public: u.company_name_public !== false,
    startup_stage: str(u.startup_stage),
    business_segment: str(u.business_segment),
    looking_for: arr(u.looking_for),
    mobile_number: str(u.mobile_number),
    newsletter_opt_in: u.newsletter_opt_in === true,
  }
}

/** PATCH /user/me/privacy — toggle public/private profile visibility. */
export async function updatePrivacy(
  is_profile_public: boolean,
): Promise<{ is_profile_public: boolean }> {
  const { data } = await api.patch<UpdatePrivacyEnvelope>('/user/me/privacy', {
    is_profile_public,
  })
  validateShape('PATCH /user/me/privacy', UpdatePrivacyResponseSchema, data)
  return data.data
}

/** POST /user/me/delete/request-otp — emails a 6-digit confirmation code. */
export async function requestAccountDeletionOtp(): Promise<void> {
  await api.post('/user/me/delete/request-otp')
}

/** DELETE /user/me — confirm + hard-delete the caller's account. */
export async function deleteMyAccount(input: {
  email: string
  otp: string
}): Promise<void> {
  await api.delete('/user/me', { data: input })
}
